import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate, statusSpacerMeta } from '../utils';
import Modal from '../components/Modal';
import { Btn } from '../components/Input';
import {
  IconEdit, IconTrash, IconLink, IconPlus, IconSnowflake,
  IconBuilding, IconPackage, IconChevronRight, IconSearch, IconAlert, IconCheck,
} from '../components/Icons';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { SkeletonCard } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { SpacerForm } from './Spacery';

const TODAY = new Date().toISOString().slice(0, 10);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function monthsBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const a = new Date(fromIso);
  const b = new Date(toIso);
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

// Ile miesięcy zostało darmowego okresu (lub null jeśli już minął / brak)
function darmowyZostalo(s) {
  if (!s.koniec_darmowego) return null;
  const m = monthsBetween(TODAY, s.koniec_darmowego);
  return m > 0 ? m : 0;
}

// ─── KPI ──────────────────────────────────────────────────────────────────────

function KPI({ label, value, sub, color, onClick, hint }) {
  const clickable = !!onClick;
  const inner = (
    <>
      <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', marginBottom: '4px' }}>{label}</p>
      <p style={{ fontSize: '20px', fontWeight: 800, fontFamily: 'monospace', color: color || 'var(--ink)' }}>{value}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{sub}</p>}
        {clickable && (
          <span style={{ color: 'var(--ink3)', flexShrink: 0, opacity: 0.6 }} title={hint}>
            <IconChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </>
  );
  if (!clickable) return <div className="ds-card p-4">{inner}</div>;
  return (
    <button onClick={onClick}
      className="ds-card p-4 transition-all"
      style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(184,84,40,0.4)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      {inner}
    </button>
  );
}

// ─── Filter pill z count'em ──────────────────────────────────────────────────

function FilterPill({ label, count, active, onClick, color }) {
  // Aktywny filtr: miękki tint w kolorze statusu (jak przyciski statusu w formularzu)
  // — kolorowe tło, ramka i tekst zamiast pełnego wypełnienia.
  const accent = color || 'var(--acc)';
  return (
    <button type="button" onClick={onClick}
      className="px-3 py-1.5 transition-colors flex items-center gap-1.5"
      style={active
        ? { background: `color-mix(in srgb, ${accent} 15%, transparent)`, color: accent, fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', borderRadius: '7px', border: `1px solid color-mix(in srgb, ${accent} 50%, transparent)` }
        : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', borderRadius: '7px', border: '1px solid var(--border)' }}>
      <span>{label}</span>
      <span style={{
        fontSize: '9px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px',
        background: active ? `color-mix(in srgb, ${accent} 22%, transparent)` : 'var(--bg2)',
        color: active ? accent : 'var(--ink3)',
        border: active ? `1px solid color-mix(in srgb, ${accent} 40%, transparent)` : '1px solid var(--border)',
        minWidth: '18px', textAlign: 'center', lineHeight: 1.4,
      }}>{count}</span>
    </button>
  );
}

// ─── Karta spaceru — kompaktowa ──────────────────────────────────────────────

function SpacerCardCompact({ s, onEdit, onDelete, onNavigate, onConfirmDeletion, t }) {
  const meta = statusSpacerMeta(s.status);
  const zgloszony = !!s.klient_zglosil_usuniecie;
  const handleClick = (e) => {
    if (e.target.closest('button')) return;
    onNavigate('spacer', s.id);
  };
  const darmoweMies = darmowyZostalo(s);
  const naroslaPositive = (s.kwota_narosla || 0) > 0;

  return (
    <div
      onClick={handleClick}
      className="hover:bg-navy-800/30 transition-colors flex items-start sm:items-center gap-3"
      style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
      }}>
      {/* Thumbnail */}
      <div style={{ flexShrink: 0, position: 'relative' }}>
        <div className="sp-thumb">
          {s.miniatura
            ? <img src={s.miniatura} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink3)' }}><IconBuilding className="w-5 h-5" /></div>
          }
        </div>
        {s.link_matterport && (
          <a href={s.link_matterport} target="_blank" rel="noreferrer"
            onClick={e => e.stopPropagation()}
            title={t('dashboardV2.openInMatterport')}
            style={{
              position: 'absolute', bottom: '-3px', right: '-3px',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: '50%', width: '20px', height: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--ink2)',
            }}>
            <IconLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Główna treść */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">
            {s.nazwa}
          </span>
          {s.data_wykonania && (
            <span style={{ fontSize: '10px', color: 'var(--ink3)', flexShrink: 0 }}>
              {formatDate(s.data_wykonania)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span style={{ fontSize: '11px', color: 'var(--ink2)' }} className="truncate">
            {s.klient_nazwa}
          </span>
          {zgloszony && (
            <span style={{ fontSize: '10px', color: 'var(--err2)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <IconAlert className="w-3 h-3" />
              {t('spaces.clientRequestedDeletion')}
            </span>
          )}
          {/* Mini-status: darmowy okres / narosła / brak zaległości */}
          {darmoweMies != null && darmoweMies > 0 && (
            <span style={{ fontSize: '10px', color: 'var(--ok2)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <IconSnowflake className="w-3 h-3" />
              {t('dashboardV2.monthsLeft', { n: darmoweMies, count: darmoweMies })}
            </span>
          )}
          {naroslaPositive && (
            <span style={{ fontSize: '10px', color: 'var(--err2)', fontWeight: 700, fontFamily: 'monospace' }}>
              {t('dashboardV2.accruedAmount', { amount: formatCurrency(s.kwota_narosla) })}
            </span>
          )}
          {!naroslaPositive && darmoweMies === null && s.status === 'aktywny' && (
            <span style={{ fontSize: '10px', color: 'var(--ok2)' }}>
              {t('dashboardV2.noAccrued')}
            </span>
          )}
        </div>
      </div>

      {/* Akcje + status */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="pill" style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
          {meta.label}
        </span>
        {zgloszony && (
          <button
            onClick={e => { e.stopPropagation(); onConfirmDeletion(s); }}
            className="w-7 h-7 flex items-center justify-center rounded-btn hover:bg-navy-800 transition-colors"
            style={{ color: 'var(--err2)', background: 'none', border: '1px solid var(--err2)', cursor: 'pointer' }}
            title={t('spaces.confirmDeletion')}
            aria-label={t('spaces.confirmDeletion')}>
            <IconCheck className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={e => { e.stopPropagation(); onEdit(s); }}
          className="w-7 h-7 flex items-center justify-center rounded-btn hover:bg-navy-800 transition-colors"
          style={{ color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label={t('common.edit')}>
          <IconEdit className="w-4 h-4" />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onDelete(s); }}
          className="w-7 h-7 flex items-center justify-center rounded-btn hover:bg-navy-800 transition-colors"
          style={{ color: 'var(--err2)', background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label={t('common.delete')}>
          <IconTrash className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Header pakietu — z paskiem wykorzystania + status ───────────────────────

function PakietHeader({ pak, collapsed, onToggle }) {
  const { t } = useTranslation();
  const used = pak.items.length;
  const cap = pak.capacity || 0;
  const pct = cap > 0 ? (used / cap) * 100 : 100;
  const full = cap > 0 && used >= cap;

  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full text-left"
      style={{
        background: 'var(--bg-card-accent)',
        border: '1px solid rgba(184,84,40,0.25)',
        borderRadius: collapsed ? '10px' : '10px 10px 0 0',
        padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: '12px',
        cursor: 'pointer',
      }}>
      <span style={{
        display: 'inline-block', flexShrink: 0,
        transition: 'transform 0.15s',
        transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
        color: 'var(--ink3)',
      }}>
        <IconChevronRight className="w-3.5 h-3.5" />
      </span>
      <IconPackage className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--acc)' }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">
            {pak.nazwa}
          </span>
          {pak.klient_nazwa && (
            <span style={{ fontSize: '11px', color: 'var(--ink3)' }} className="truncate">· {pak.klient_nazwa}</span>
          )}
        </div>
        {/* Pasek wykorzystania */}
        {cap > 0 && (
          <div className="flex items-center gap-2 mt-1.5">
            <div style={{ flex: 1, height: '4px', background: 'var(--bg4)', borderRadius: '2px', overflow: 'hidden', maxWidth: '180px' }}>
              <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: full ? 'var(--err2)' : 'var(--acc)', borderRadius: '2px' }} />
            </div>
          </div>
        )}
      </div>
      <span style={{
        fontSize: '10px', fontWeight: 700,
        padding: '4px 10px', borderRadius: '999px',
        background: full ? 'rgba(176,88,72,0.15)' : 'rgba(90,158,114,0.15)',
        color: full ? 'var(--err2)' : 'var(--ok2)',
        border: full ? '1px solid rgba(176,88,72,0.3)' : '1px solid rgba(90,158,114,0.3)',
        flexShrink: 0,
      }}>
        {used}/{cap || '∞'} {t('dashboardV2.spaceWordOnly', { count: used })}
      </span>
    </button>
  );
}

// ─── KPI breakdown modal ──────────────────────────────────────────────────────

function KPIDetailModal({ type, spacery, onClose, onNavigate }) {
  const { t } = useTranslation();
  if (!type) return null;

  const aktywne = spacery.filter(s => s.status === 'aktywny');

  let title = '';
  let items = [];
  let renderItem = () => null;

  if (type === 'aktywne') {
    title = `${t('dashboardV2.modalActiveSpaces')} (${aktywne.length})`;
    items = [...aktywne].sort((a, b) => (a.nazwa || '').localeCompare(b.nazwa || ''));
    renderItem = s => (
      <div className="flex items-center justify-between gap-3" key={s.id}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{s.nazwa}</span>
        <span style={{ fontSize: '10px', color: 'var(--ink3)' }} className="flex-shrink-0">{s.klient_nazwa}</span>
      </div>
    );
  }
  if (type === 'mrr') {
    title = t('dashboardV2.modalMRR');
    items = [...aktywne].sort((a, b) => (b.stawka_hostingu || 0) - (a.stawka_hostingu || 0));
    renderItem = s => (
      <div className="flex items-center justify-between gap-3" key={s.id}>
        <div className="min-w-0">
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate block">{s.nazwa}</span>
          <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{s.klient_nazwa}</span>
        </div>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ok2)', fontSize: '12px' }}>
          {formatCurrency(s.stawka_hostingu || 0)}{t('dashboardV2.perMonthShort')}
        </span>
      </div>
    );
  }
  if (type === 'darmowy') {
    title = t('dashboardV2.kpiInDarmowy');
    items = aktywne
      .filter(s => darmowyZostalo(s) > 0)
      .sort((a, b) => darmowyZostalo(a) - darmowyZostalo(b));
    renderItem = s => {
      const m = darmowyZostalo(s);
      const ending = m <= 1;
      return (
        <div className="flex items-center justify-between gap-3" key={s.id}>
          <div className="min-w-0">
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate block">{s.nazwa}</span>
            <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{s.klient_nazwa}</span>
          </div>
          <span style={{ fontSize: '11px', color: ending ? 'var(--acc)' : 'var(--ok2)', fontWeight: 700 }}>
            {m} {t('dashboardV2.monthsShort')}
            {ending && <IconAlert className="w-3 h-3 inline-block ml-1" />}
          </span>
        </div>
      );
    };
  }
  if (type === 'narosle') {
    title = t('dashboardV2.kpiAccrued');
    items = aktywne
      .filter(s => (s.kwota_narosla || 0) > 0)
      .sort((a, b) => (b.kwota_narosla || 0) - (a.kwota_narosla || 0));
    renderItem = s => (
      <div className="flex items-center justify-between gap-3" key={s.id}>
        <div className="min-w-0">
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate block">{s.nazwa}</span>
          <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{s.klient_nazwa}</span>
        </div>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--err2)', fontSize: '12px' }}>
          {formatCurrency(s.kwota_narosla)}
        </span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Modal title={title} onClose={onClose}>
        <div className="text-center" style={{ padding: '24px 0', color: 'var(--ink3)', fontSize: '12px' }}>
          {t('dashboardV2.modalNoItems')}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col">
        {items.map(s => (
          <button
            key={s.id}
            onClick={() => { onClose(); onNavigate('spacer', s.id); }}
            className="text-left w-full"
            style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', background: 'none', border: 'none', cursor: 'pointer' }}>
            {renderItem(s)}
          </button>
        ))}
      </div>
    </Modal>
  );
}

// ─── Główny SpaceryV2 ─────────────────────────────────────────────────────────

export default function SpaceryV2({ onNavigate }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [spacery, setSpacery] = useState([]);
  const [klienci, setKlienci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('aktywny');
  const [klientFilter, setKlientFilter] = useState('');
  const [pakietFilter, setPakietFilter] = useState(''); // '' | 'pakiet' | 'solo'
  const [sortBy, setSortBy] = useState('data'); // 'data' | 'nazwa' | 'narosla' | 'stawka'
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [collapsedPackages, setCollapsedPackages] = useState({});
  const [kpiModal, setKpiModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getSpacery({}), api.getKlienci()])
      .then(([s, k]) => { setSpacery(s); setKlienci(k); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async form => {
    await api.addSpacer(form);
    load();
    toast.success(t('toast.added'));
  };
  const handleEditSubmit = async form => {
    await api.updateSpacer(modal.edit.id, form);
    load();
    toast.success(t('toast.saved'));
  };
  const handleDelete = async s => {
    const ok = await confirm({
      title: t('confirmDialog.deleteTitle'),
      message: t('spaces.deleteConfirm', { name: s.nazwa }),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteSpacer(s.id);
      toast.success(t('toast.deleted'));
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };
  // Klient zgłosił spacer do usunięcia — potwierdzenie nadaje status 'usuniety'
  // (serwer sam ustawia datę usunięcia i czyści flagę zgłoszenia). Spacer zostaje w historii.
  const handleConfirmDeletion = async s => {
    const ok = await confirm({
      title: t('spaces.confirmDeletion'),
      message: t('spaces.confirmDeletionMsg', { name: s.nazwa }),
      confirmLabel: t('spaces.confirmDeletion'),
      cancelLabel: t('common.cancel'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.updateSpacer(s.id, { ...s, status: 'usuniety' });
      toast.success(t('toast.saved'));
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Counts dla pills (uwzględniają search/klientFilter, ale nie filterStatus)
  const baseFiltered = useMemo(() => spacery.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      const ok = (s.nazwa || '').toLowerCase().includes(q)
        || (s.adres || '').toLowerCase().includes(q)
        || (s.klient_nazwa || '').toLowerCase().includes(q)
        || (s.pakiet_nazwa || '').toLowerCase().includes(q);
      if (!ok) return false;
    }
    if (klientFilter && String(s.klient_id) !== String(klientFilter)) return false;
    if (pakietFilter === 'pakiet' && !s.pakiet_id) return false;
    if (pakietFilter === 'solo' && s.pakiet_id) return false;
    return true;
  }), [spacery, search, klientFilter, pakietFilter]);

  const counts = useMemo(() => {
    const c = { all: baseFiltered.length };
    baseFiltered.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [baseFiltered]);

  // Final filtered (po statusie)
  const filtered = useMemo(() => {
    let arr = baseFiltered.filter(s => !filterStatus || s.status === filterStatus);
    if (sortBy === 'nazwa') arr = [...arr].sort((a, b) => (a.nazwa || '').localeCompare(b.nazwa || ''));
    if (sortBy === 'narosla') arr = [...arr].sort((a, b) => (b.kwota_narosla || 0) - (a.kwota_narosla || 0));
    if (sortBy === 'stawka') arr = [...arr].sort((a, b) => (b.stawka_hostingu || 0) - (a.stawka_hostingu || 0));
    if (sortBy === 'data') arr = [...arr].sort((a, b) => (b.data_wykonania || '').localeCompare(a.data_wykonania || ''));
    return arr;
  }, [baseFiltered, filterStatus, sortBy]);

  // KPI calc na pełnym datasecie spacery (nie filtrowane — globalna stat zone)
  const stats = useMemo(() => {
    const aktywne = spacery.filter(s => s.status === 'aktywny');
    const mrr = aktywne.reduce((s, x) => s + (x.stawka_hostingu || 0), 0);
    const naroslaLacznie = aktywne.reduce((s, x) => s + (x.kwota_narosla || 0), 0);
    const wDarmowym = aktywne.filter(s => darmowyZostalo(s) > 0);
    const koniecBlisko = wDarmowym.filter(s => darmowyZostalo(s) <= 1);
    return {
      aktywne: aktywne.length,
      mrr,
      naroslaLacznie,
      wDarmowymCount: wDarmowym.length,
      koniecBliskoCount: koniecBlisko.length,
      naroslaCount: aktywne.filter(s => (s.kwota_narosla || 0) > 0).length,
    };
  }, [spacery]);

  // Grupowanie po pakiecie
  const grouped = useMemo(() => {
    const packages = new Map();
    const solo = [];
    filtered.forEach(s => {
      if (s.pakiet_id) {
        if (!packages.has(s.pakiet_id)) {
          packages.set(s.pakiet_id, {
            id: s.pakiet_id,
            nazwa: s.pakiet_nazwa || t('dashboardV2.packageWithId', { id: s.pakiet_id }),
            capacity: s.pakiet_pojemnosc || 0,
            klient_nazwa: s.klient_nazwa,
            items: [],
          });
        }
        packages.get(s.pakiet_id).items.push(s);
      } else {
        solo.push(s);
      }
    });
    return { packages: Array.from(packages.values()), solo };
  }, [filtered, t]);

  return (
    <div>
      {kpiModal && (
        <KPIDetailModal
          type={kpiModal}
          spacery={spacery}
          onClose={() => setKpiModal(null)}
          onNavigate={onNavigate}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>{t('spaces.title')}</h1>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>{t('dashboardV2.spaceCountTotal', { n: spacery.length, count: spacery.length })}</p>
        </div>
        <Btn onClick={() => setModal('add')} disabled={klienci.length === 0}>
          <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('spaces.add')}</span>
        </Btn>
      </div>

      {klienci.length === 0 && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(184,84,40,0.08)', border: '1px solid rgba(184,84,40,0.25)', fontSize: '11px', color: 'var(--acc2)' }}>
          {t('spaces.noClientWarning')}
        </div>
      )}

      {/* Stat zone */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <KPI
          label={t('dashboardV2.kpiActiveSpaces')}
          value={String(stats.aktywne)}
          color="var(--ink)"
          sub={t('dashboardV2.subOf', { total: spacery.length })}
          onClick={stats.aktywne > 0 ? () => setKpiModal('aktywne') : undefined}
        />
        <KPI
          label={t('dashboardV2.kpiMRR')}
          value={formatCurrency(stats.mrr)}
          color="var(--ok2)"
          sub={t('dashboardV2.kpiMRRSub')}
          onClick={stats.aktywne > 0 ? () => setKpiModal('mrr') : undefined}
        />
        <KPI
          label={t('dashboardV2.kpiInDarmowy')}
          value={String(stats.wDarmowymCount)}
          color={stats.koniecBliskoCount > 0 ? 'var(--acc2)' : 'var(--ok2)'}
          sub={stats.koniecBliskoCount > 0
            ? t('dashboardV2.endingInDays', { n: stats.koniecBliskoCount, count: stats.koniecBliskoCount })
            : t('dashboardV2.kpiInDarmowyAllSet')}
          onClick={stats.wDarmowymCount > 0 ? () => setKpiModal('darmowy') : undefined}
        />
        <KPI
          label={t('dashboardV2.kpiAccrued')}
          value={formatCurrency(stats.naroslaLacznie)}
          color={stats.naroslaLacznie > 0 ? 'var(--err2)' : 'var(--ok2)'}
          sub={stats.naroslaCount > 0
            ? `${stats.naroslaCount} ${t('dashboardV2.spaceWordOnly', { count: stats.naroslaCount })}`
            : t('dashboardV2.kpiAccruedNone')}
          onClick={stats.naroslaLacznie > 0 ? () => setKpiModal('narosle') : undefined}
        />
      </div>

      {/* Filtry — pierwszy wiersz: search + dropdown klient + dropdown sort */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex-1 min-w-[200px] relative">
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--ink3)', pointerEvents: 'none' }}>
            <IconSearch className="w-3.5 h-3.5" />
          </span>
          <input
            className="ds-input"
            placeholder={t('dashboardV2.searchSpacesPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: '32px' }}
          />
        </div>
        <select value={klientFilter} onChange={e => setKlientFilter(e.target.value)}
          className="ds-input" style={{ width: 'auto', minWidth: '160px', background: 'var(--bg2)' }}>
          <option value="">{t('dashboardV2.filterAllClients')}</option>
          {klienci.map(k => <option key={k.id} value={k.id}>{k.nazwa}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="ds-input" style={{ width: 'auto', background: 'var(--bg2)' }}>
          <option value="data">{t('dashboardV2.sortByDate')}</option>
          <option value="nazwa">{t('dashboardV2.sortByName')}</option>
          <option value="narosla">{t('dashboardV2.sortByDebt')}</option>
          <option value="stawka">{t('dashboardV2.sortByRate')}</option>
        </select>
        {(search || klientFilter || pakietFilter || sortBy !== 'data') && (
          <button
            onClick={() => { setSearch(''); setKlientFilter(''); setPakietFilter(''); setSortBy('data'); }}
            style={{ fontSize: '10px', color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            {t('dashboardV2.filterClearShort')}
          </button>
        )}
      </div>

      {/* Filtry — drugi wiersz: status pills + typ */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <FilterPill label={t('spaces.active_filter')}    count={counts.aktywny || 0}    active={filterStatus === 'aktywny'}    onClick={() => setFilterStatus('aktywny')} color="var(--ok2)" />
        <FilterPill label={t('spaces.all')}              count={counts.all}              active={filterStatus === ''}            onClick={() => setFilterStatus('')} />
        {(counts.zamrozony || 0) > 0 && <FilterPill label={t('spaces.frozen_filter')}    count={counts.zamrozony}    active={filterStatus === 'zamrozony'}    onClick={() => setFilterStatus('zamrozony')} color="#4a9eba" />}
        {(counts.przekazany || 0) > 0 && <FilterPill label={t('spaces.transferred_filter')} count={counts.przekazany} active={filterStatus === 'przekazany'} onClick={() => setFilterStatus('przekazany')} color="#b48ad8" />}
        {(counts.usuniety || 0) > 0 && <FilterPill label={t('spaces.deleted_filter')} count={counts.usuniety}    active={filterStatus === 'usuniety'}    onClick={() => setFilterStatus('usuniety')} color="var(--err2)" />}

        <span style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />

        <FilterPill label={t('dashboardV2.filterTypePakiet')} count={spacery.filter(s => s.pakiet_id).length}  active={pakietFilter === 'pakiet'} onClick={() => setPakietFilter(pakietFilter === 'pakiet' ? '' : 'pakiet')} />
        <FilterPill label={t('dashboardV2.filterTypeSolo')}   count={spacery.filter(s => !s.pakiet_id).length} active={pakietFilter === 'solo'}   onClick={() => setPakietFilter(pakietFilter === 'solo' ? '' : 'solo')} />
      </div>

      {loading && (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={2} />)}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <EmptyState
          icon={<IconBuilding className="w-12 h-12" />}
          title={search || klientFilter || pakietFilter || filterStatus
            ? t('dashboardV2.noFilteredSpaces')
            : t('spaces.noSpaces')}
          description={search || klientFilter || pakietFilter || filterStatus
            ? t('dashboardV2.noFilteredHint')
            : t('spaces.noSpacesHint')}
          action={(search || klientFilter || pakietFilter || (filterStatus && filterStatus !== 'aktywny')) ? (
            <Btn variant="ghost" onClick={() => { setSearch(''); setKlientFilter(''); setPakietFilter(''); setFilterStatus('aktywny'); }}>
              {t('dashboardV2.filterClear')}
            </Btn>
          ) : (
            <Btn onClick={() => setModal('add')}>
              <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('spaces.addFirst')}</span>
            </Btn>
          )}
        />
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex flex-col gap-4">
          {/* Pakiety */}
          {grouped.packages.map(pak => {
            const collapsed = !!collapsedPackages[pak.id];
            return (
              <div key={`pak-${pak.id}`} className="ds-card overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <PakietHeader
                  pak={pak}
                  collapsed={collapsed}
                  onToggle={() => setCollapsedPackages(c => ({ ...c, [pak.id]: !collapsed }))}
                />
                {!collapsed && (
                  <div>
                    {pak.items.map(s => (
                      <SpacerCardCompact
                        key={s.id}
                        s={s}
                        onEdit={s => setModal({ edit: s })}
                        onDelete={handleDelete}
                        onConfirmDeletion={handleConfirmDeletion}
                        onNavigate={onNavigate}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Solo spacery */}
          {grouped.solo.length > 0 && (
            <div className="ds-card overflow-hidden">
              {grouped.packages.length > 0 && (
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg2)' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('dashboardV2.spacesRowSolo', { n: grouped.solo.length })}
                  </span>
                </div>
              )}
              {grouped.solo.map(s => (
                <SpacerCardCompact
                  key={s.id}
                  s={s}
                  onEdit={s => setModal({ edit: s })}
                  onDelete={handleDelete}
                  onConfirmDeletion={handleConfirmDeletion}
                  onNavigate={onNavigate}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modale */}
      {modal === 'add' && (
        <Modal title={t('spaces.add')} onClose={() => setModal(null)} wide>
          <SpacerForm
            klienci={klienci}
            onSave={handleAdd}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
      {modal?.edit && (
        <Modal title={t('spaces.editSettings')} onClose={() => setModal(null)} wide>
          <SpacerForm
            klienci={klienci}
            initial={{ ...modal.edit, przelane_wykonanie: !!modal.edit.przelane_wykonanie }}
            onSave={handleEditSubmit}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
    </div>
  );
}
